import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import GenericNav from '../../../components/genericcomponents/navbars/GenericNav'
import {
  Box,
  Backdrop,
  Container,
  CircularProgress,
  Grid,
  Typography,
} from '../../../components/mui/Core/index'
import '../outboundorders.css'
import ShippingDetailsCard from './components/ShippingDetailsCard/shipping-details-card'
import BillingDetailsCard from '../components/BillingDetailsCard/billing-details-card'
import { fetchShipments } from './order-list-slice'
import StatusChip from '../../../components/genericcomponents/StatusChip/status-chip'
import ShipmentTable from './components/ShipmentTable/shipment-table'
import DmsOrderLineTable from './components/DmsOrderLineTable/dms-order-line-table'
import DmsOrderHoldDetailsTable from './components/DmsOrderHoldDetailsTable/dms-order-hold-details-table'
import GenericSnackbar from '../../../components/GenericSnackbar/generic-snackbar'
import TableSearch from '../../../components/TableSearch/table-search'
import useBrowserTabTitles from '../../../hooks/browserTabTitles/browser-tab-titles'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import PageTitle from '../../../components/PageTitle/page-title'
import Constants from '../../../constants/en.json'
import SubHeader from '../../../components/SubHeader/sub-header'
import { sessionStorageKeyConstants } from '../../../constants/session-storage-key-constants'
import { useTheme } from '../../../components/mui/Styles/index'
import { orderListConstants } from '../../../constants/order-list-constants'
import { WMS_ORDER_AND_SHIPMENT_STATUS_OPTIONS } from '../../../constants/status'

function OrderList() {
  const theme = useTheme()
  const dispatch = useDispatch()
  const isFetchingShipments = useSelector((state) => state.orderList.isFetchingShipments)
  const fetchShipmentsError = useSelector((state) => state.orderList.fetchShipmentsError)
  const selectedOrder = useSelector((state) => state.order.selectedOrder)
  const orderDetails = useSelector((state) => state.orderList.orderDetails)
  const dmsOrderHoldDetails = useSelector((state) => state.orderList.dmsOrderHoldDetails)
  const shipments = useSelector((state) => state.orderList.shipments)
  const shipmentCount = shipments.length || 0
  const searchByOptions = [
    { id: 'wmsOrderNum', text: 'Shipment', type: 'string' },
    { id: 'orderStatus', text: 'Status', type: 'dropdown' },
    { id: 'createdOn', text: 'Created on', type: 'date' },
    { id: 'dateShipped', text: 'Date Shipped', type: 'date' },
  ]
  const searchByOptionsDmsLineOrder = [
    { id: 'itemNumber', text: 'NDC/Item #', type: 'string' },
    { id: 'itemDescription', text: 'Item Description', type: 'string' },
  ]
  const [tableFilterValue, setTableFilterValue] = useState('')
  const [tableFilterBy, setTableFilterBy] = useState(searchByOptions[0].id)
  const [tableFilterValueDmsLineOrder, setTableFilterValueDmsLineOrder] = useState('')
  const [tableFilterByDmsLineOrder, setTableFilterByDmsLineOrder] = useState(
    searchByOptionsDmsLineOrder[0].id,
  )
  const selectedClient = JSON.parse(
    sessionStorage.getItem(sessionStorageKeyConstants.SELECTED_CLIENT),
  )
  const pageTitle = selectedClient?.clientShortName
    ? `${selectedClient?.clientShortName} - ${Constants.orders.ORDER} ${selectedOrder?.dms_order}`
    : `${Constants.orders.ORDER} ${selectedOrder?.dms_order}`

  const browserTabTitle = selectedClient?.clientShortName
    ? `${selectedClient?.clientShortName} - ${Constants.orders.ORDER} ${selectedOrder.dms_order}`
    : `${Constants.orders.ORDER} ${selectedOrder.dms_order}`
  useBrowserTabTitles(browserTabTitle)

  useEffect(() => {
    dispatch(fetchShipments())
  }, [dispatch])

  useEffect(() => {
    if (fetchShipmentsError) {
      dispatch(
        setSnackbar({
          message: fetchShipmentsError?.message,
          severity: 'error',
          action: true,
        }),
      )
    }
  }, [fetchShipmentsError, dispatch])

  const returnSearch = (inputValue, selectedMenuItemIndex) => {
    setTableFilterValue(inputValue)
    setTableFilterBy(searchByOptions[selectedMenuItemIndex].id)
  }

  const returnSearchDmsLineOrder = (inputValue, selectedMenuItemIndex) => {
    setTableFilterValueDmsLineOrder(inputValue)
    setTableFilterByDmsLineOrder(searchByOptionsDmsLineOrder[selectedMenuItemIndex].id)
  }

  const titleWrapperStyle = {
    display: 'flex',
  }

  const statusMessageStyle = {
    pb: '10px',
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <GenericNav selected='Orders' user='authenticated' />
      <Backdrop open={isFetchingShipments}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Container maxWidth='xl'>
        <Grid container spacing={3} rowSpacing={1}>
          <Grid size={{ xs: 12 }} sx={titleWrapperStyle}>
            <PageTitle title={pageTitle} />
            <Box sx={{ ml: 3, alignSelf: 'center' }}>
              <StatusChip status={selectedOrder?.orderStatus} />
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }} sx={statusMessageStyle}>
            <Typography sx={theme.message}>{orderDetails?.orderStatusNotification}</Typography>
          </Grid>
          <Grid size={{ xs: 3 }}>
            <BillingDetailsCard orderDetails={orderDetails ?? {}} />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <ShippingDetailsCard />
          </Grid>
        </Grid>
        <Grid container maxWidth='xl'>
          {!isFetchingShipments && dmsOrderHoldDetails?.length > 0 && (
            <Grid size={{ xs: 12 }} sx={{ mt: '28px' }}>
              <Grid size={{ xs: 10 }}>
                <SubHeader title={Constants.orders.order_details.HOLD_DETAILS} />
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ mt: '10px' }}>
                <DmsOrderHoldDetailsTable />
              </Grid>
            </Grid>
          )}
          {!isFetchingShipments && shipmentCount > 0 && (
            <Grid container sx={{ mt: '28px' }}>
              <Grid size={{ xs: 12 }} display='flex'>
                <Grid size={{ xs: 10 }}>
                  <SubHeader title={Constants.orders.SHIPMENTS_ASSOCIATED} />
                </Grid>
                <Grid size={{ xs: 4 }} sx={{ display: 'flex', justifyContent: 'end' }}>
                  <TableSearch
                    returnSearch={returnSearch}
                    searchByOptions={searchByOptions}
                    statusOptions={WMS_ORDER_AND_SHIPMENT_STATUS_OPTIONS}
                  />
                </Grid>
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ mt: '10px' }}>
                <ShipmentTable
                  filterValue={tableFilterValue}
                  filterBy={tableFilterBy}
                  searchByOptions={searchByOptions}
                />
              </Grid>
            </Grid>
          )}
          {!isFetchingShipments && shipmentCount === 0 && (
            <Grid container sx={{ mt: '28px' }}>
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Typography variant='message'>
                    {orderListConstants.NO_SHIPMENTS_ASSIGNED}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }} display='flex' sx={{ mt: '28px' }}>
                <Grid size={{ xs: 10 }}>
                  <SubHeader title={Constants.orders.ORDER_LINE_DETAILS} />
                </Grid>
                <Grid size={{ xs: 4 }} sx={{ display: 'flex', justifyContent: 'end' }}>
                  <TableSearch
                    returnSearch={returnSearchDmsLineOrder}
                    searchByOptions={searchByOptionsDmsLineOrder}
                    statusOptions={WMS_ORDER_AND_SHIPMENT_STATUS_OPTIONS}
                  />
                </Grid>
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ mt: '10px' }}>
                <DmsOrderLineTable
                  filterValue={tableFilterValueDmsLineOrder}
                  filterBy={tableFilterByDmsLineOrder}
                  searchByOptions={searchByOptionsDmsLineOrder}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Container>
      <GenericSnackbar />
    </Box>
  )
}

export default OrderList
